import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Search, ArrowLeft, Clock } from "lucide-react";

const TOPICS = [
  { v: "all", label: "All" },
  { v: "sleep", label: "Sleep" },
  { v: "mood", label: "Mood" },
  { v: "brain-fog", label: "Brain fog" },
  { v: "hot-flashes", label: "Hot flashes" },
  { v: "hrt", label: "HRT" },
  { v: "doctor", label: "Doctor" },
  { v: "body", label: "Body" },
];

export default function Library() {
  const [articles, setArticles] = useState([]);
  const [topic, setTopic] = useState("all");
  const [q, setQ] = useState("");
  const [reading, setReading] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/library");
      setArticles(data);
    })();
  }, []);

  const filtered = useMemo(
    () =>
      articles.filter((a) => {
        const matchTopic = topic === "all" || a.topic === topic;
        const matchQ =
          !q ||
          a.title.toLowerCase().includes(q.toLowerCase()) ||
          a.excerpt.toLowerCase().includes(q.toLowerCase());
        return matchTopic && matchQ;
      }),
    [articles, topic, q]
  );

  const openArticle = async (id) => {
    const { data } = await api.get(`/library/${id}`);
    setReading(data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (reading) {
    return (
      <div className="max-w-2xl mx-auto rise">
        <button
          onClick={() => setReading(null)}
          className="inline-flex items-center gap-2 text-[#52443E] hover:text-[#2C221E] mb-6"
          data-testid="library-back-button"
        >
          <ArrowLeft size={16} /> Back to library
        </button>
        <p className="text-xs uppercase tracking-[0.22em] text-[#8C7A70] font-semibold">
          {reading.topic.replace("-", " ")}
        </p>
        <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#2C221E] leading-tight" data-testid="library-article-title">
          {reading.title}
        </h1>
        <p className="mt-4 inline-flex items-center gap-2 text-sm text-[#8C7A70]">
          <Clock size={14} /> {reading.read_minutes} min read
        </p>
        <div className="mt-8 text-[#2C221E] text-lg leading-[1.8] whitespace-pre-wrap font-serif-display" data-testid="library-article-body">
          {reading.body}
        </div>
      </div>
    );
  }

  return (
    <div className="rise">
      <p className="text-xs uppercase tracking-[0.22em] text-[#8C7A70] font-semibold">
        Reading room
      </p>
      <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl text-[#2C221E] leading-tight" data-testid="library-title">
        A library for the questions you did not know to ask.
      </h1>
      <p className="mt-3 text-[#52443E] max-w-2xl">
        Short, honest reads grounded in current menopause science. No sensationalism, no scare tactics — just the knowing.
      </p>

      <div className="mt-8 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8C7A70]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the library"
            className="input-paper !pl-11"
            data-testid="library-search-input"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {TOPICS.map((t) => (
          <button
            key={t.v}
            className="chip"
            data-active={topic === t.v}
            onClick={() => setTopic(t.v)}
            data-testid={`library-topic-${t.v}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {filtered.map((a) => (
          <button
            key={a.id}
            onClick={() => openArticle(a.id)}
            className="card-paper p-7 text-left paper-grain transition-transform hover:-translate-y-0.5"
            data-testid={`library-article-${a.id}`}
          >
            <p className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">
              {a.topic.replace("-", " ")}
            </p>
            <h3 className="mt-3 font-serif-display text-2xl text-[#2C221E] leading-snug">
              {a.title}
            </h3>
            <p className="mt-3 text-[#52443E] leading-relaxed">{a.excerpt}</p>
            <p className="mt-5 inline-flex items-center gap-2 text-sm text-[#8C7A70]">
              <Clock size={14} /> {a.read_minutes} min read
            </p>
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="text-[#73635B] col-span-full">Nothing matches that just yet.</p>
        )}
      </div>
    </div>
  );
}
